import { jsx, jsxs } from 'react/jsx-runtime';
import { forwardRef, useState, useRef, useImperativeHandle, useEffect } from 'react';
import { common } from '../../../assets/js/common.js';
import { Input } from '../../ui/input.js';
import { useInputCaret } from '../../../hooks/use-input-caret.js';
import { cn } from '../../../lib/utils.js';

const PriceInput = forwardRef(
  ({
    value,
    onChange,
    currency = "원",
    error = false,
    nextInputRef,
    unitText = "영",
    typeText = false,
    ...props
  }, ref) => {
    const [displayValue, setDisplayValue] = useState(
      Number(value) > 0 ? Number(value).toLocaleString() : ""
    );
    const [koreanAmount, setKoreanAmount] = useState(`${unitText} ${currency}`);
    const inputRef = useRef(null);
    useImperativeHandle(ref, () => inputRef.current, []);
    const setAllValue = (value2) => {
      setDisplayValue(value2);
      onChange?.(value2);
    };
    const { handleChange, handleKeyDown } = useInputCaret({
      inputRef,
      value: displayValue,
      format: common.setFormatPrice,
      onValueChange: setAllValue,
      variants: "price",
      nextInputRef
    });
    const numberToKorean = (num) => {
      if (num === 0) return `영 ${currency}`;
      const digits = ["", "일", "이", "삼", "사", "오", "육", "칠", "팔", "구"];
      const units = ["", "만", "억", "조"];
      const smallUnits = ["", "십", "백", "천"];
      let result = "";
      let unitIndex = 0;
      while (num > 0) {
        const part = num % 1e4;
        if (part > 0) {
          let partStr = "";
          let tempPart = part;
          let smallUnitIndex = 0;
          while (tempPart > 0) {
            const digit = tempPart % 10;
            if (digit > 0) {
              partStr = digits[digit] + smallUnits[smallUnitIndex] + partStr;
            }
            tempPart = Math.floor(tempPart / 10);
            smallUnitIndex++;
          }
          result = partStr + units[unitIndex] + (result ? " " : "") + result;
        }
        num = Math.floor(num / 1e4);
        unitIndex++;
      }
      return `${result.trim()} ${currency}`;
    };
    useEffect(() => {
      setDisplayValue(value ?? "");
      setKoreanAmount(
        displayValue && displayValue !== "" ? numberToKorean(parseInt(displayValue.replace(/,/g, ""), 10)) : `${unitText} ${currency}`
      );
    }, [displayValue, value]);
    return /* @__PURE__ */ jsx(
      "div",
      {
        className: "flex flex-col gap-2 has-[input:invalid]:border-interactive-danger",
        children: /* @__PURE__ */ jsxs(
          "div",
          {
            className: cn(
              "flex flex-col border border-input rounded-lg bg-base-white border-basic-04 p-4 md:p-5",
              "focus-within:border-interactive-primary-default-01 focus-within:shadow-[inset_0_0_0_1px_var(--color-border-interactive-primary-default-01)]",
              "has-[input:read-only]:bg-state-disabled-02 has-[input:read-only]:text-basic-02 has-[input:read-only]:border-state-disabled-03 [&:has(input:read-only)_span]:text-basic-02 [&:has(input:read-only)_p]:text-basic-02",
              error ? "!border-interactive-danger !shadow-[inset_0_0_0_1px_var(--color-border-interactive-danger)]" : ""
            ),
            children: [
              /* @__PURE__ */ jsxs(
                "div",
                {
                  className: cn(
                    "inline-flex items-stretch justify-end gap-0.5",
                    common.checkCurrency(currency) ? "flex-row" : "flex-row-reverse"
                    // 통화코드 순서
                  ),
                  children: [
                    /* @__PURE__ */ jsx(
                      Input,
                      {
                        ref: inputRef,
                        type: "text",
                        placeholder: props.placeholder ? props.placeholder : "0",
                        maxLength: 16,
                        onChange: handleChange,
                        onKeyDown: handleKeyDown,
                        value: displayValue,
                        inputMode: "numeric",
                        ...props,
                        className: cn(
                          "!eng-heading-h3 pt-[1px] text-primary-05 font-weight-medium text-right placeholder:text-primary-04 placeholder:!eng-heading-h3 peer read-only:placeholder:text-state-disabled-04 read-only:text-basic-02"
                        ),
                        enterKeyHint: props.enterKeyHint
                      }
                    ),
                    /* @__PURE__ */ jsx(
                      "span",
                      {
                        className: cn(
                          "heading-h4 text-primary-05 flex-[0_0_auto]"
                        ),
                        children: typeText === true ? !common.checkCurrency(currency) ? "" : `${currency}` : `${currency}`
                      }
                    )
                  ]
                }
              ),
              typeText === true ? !common.checkCurrency(currency) ? /* @__PURE__ */ jsx("p", { className: "body-md text-basic-04 text-right mt-[2px]", children: "(Unit: KRW 10,000)" }) : /* @__PURE__ */ jsx("p", { className: "body-md text-basic-04 text-right mt-[2px]", children: koreanAmount || `${unitText} ${currency}` }) : !common.checkCurrency(currency) ? "" : /* @__PURE__ */ jsx("p", { className: "body-md text-basic-04 text-right mt-[2px]", children: koreanAmount || `${unitText} ${currency}` })
            ]
          }
        )
      }
    );
  }
);

export { PriceInput };
